import { type ConnectLibraryAnswer } from "@/lib/api/connect-library";
import { useConnectQuestion } from "@/lib/hooks/use-connect-definition";
import { cn } from "@/lib/utils";
import { colorsV2 } from "@somethings-ui/core";
import { Check } from "lucide-react";
import { isExpandedState, RowAudioRecorder } from "./row-audio-recorder";
import {
  useConnectResponderRow,
  type ConfirmedResponderAnswer,
} from "./use-connect-responder-row";

interface ConnectResponderRowProps {
  entityId: string;
  index: 0 | 1 | 2;
  questionId: string;
  libraryAnswer: ConnectLibraryAnswer | null;
  isAnotherRowRecording: boolean;
  micDeviceId: string | null;
  onRecordingStateChange: (isRecording: boolean) => void;
  onConfirmedChange: (confirmed: ConfirmedResponderAnswer | null) => void;
}

export function ConnectResponderRow({
  entityId,
  index,
  questionId,
  libraryAnswer,
  isAnotherRowRecording,
  micDeviceId,
  onRecordingStateChange,
  onConfirmedChange,
}: ConnectResponderRowProps) {
  const question = useConnectQuestion(questionId);
  const row = useConnectResponderRow({
    entityId,
    index,
    questionId,
    libraryAnswer,
    micDeviceId,
    onRecordingStateChange,
    onConfirmedChange,
  });

  const expanded = isExpandedState(row.uiState);
  const levelLabel = `0${index + 1}`;

  return (
    <div
      data-testid={`connect-responder-row-${index}`}
      className={cn(
        "flex flex-col rounded-xl transition-colors",
        expanded ? "px-4 py-3" : "px-1 py-2",
      )}
      style={expanded ? { background: colorsV2.surfaces.z3 } : undefined}
    >
      <div className="flex items-center gap-3">
        <span
          className="font-mono text-xs tabular-nums"
          style={{ color: colorsV2.text.tertiary }}
        >
          {levelLabel}
        </span>
        <p
          className="flex-1 text-sm font-medium leading-snug"
          style={{ color: colorsV2.text.primary }}
        >
          {question?.text ?? "…"}
        </p>
        {row.hasUnconfirmedLibraryAnswer && (
          <button
            type="button"
            onClick={row.confirmLibraryAnswer}
            disabled={
              row.isConfirmingLibraryAnswer ||
              isAnotherRowRecording ||
              row.uiState.kind !== "completed"
            }
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-surfaces-z5 px-3 py-1.5 text-xs font-bold hover:bg-surfaces-z2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ color: colorsV2.text.primary }}
          >
            <Check className="h-3.5 w-3.5" />
            {row.isConfirmingLibraryAnswer ? "Saving…" : "Use this answer"}
          </button>
        )}
        {row.confirmed && (
          <span
            aria-label={`Level ${index + 1} answer confirmed`}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
            style={{ background: colorsV2.brand.tangelo }}
          >
            <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
          </span>
        )}
        {!expanded && (
          <RowAudioRecorder
            state={row.uiState}
            otherRowRecording={isAnotherRowRecording}
            onStartRecording={row.start}
            onStopRecording={row.stop}
            onPlay={row.play}
            onPause={row.pause}
            onDelete={row.clear}
            onReRecord={row.reRecord}
            onRetryPermission={row.retryPermission}
          />
        )}
      </div>
      {expanded && (
        <RowAudioRecorder
          state={row.uiState}
          otherRowRecording={isAnotherRowRecording}
          onStartRecording={row.start}
          onStopRecording={row.stop}
          onPlay={row.play}
          onPause={row.pause}
          onDelete={row.clear}
          onReRecord={row.reRecord}
          onRetryPermission={row.retryPermission}
        />
      )}
    </div>
  );
}
